
import { ApiService } from "Services/ApiService";

// Define types for dashboard data
export interface DashboardStats {
  totalRevenue: number;
  revenueChange: number;
  orders: number;
  ordersChange: number;
  uniqueVisits: number;
  uniqueVisitsChange: number;
  newUsers: number;
  newUsersChange: number;
  existingUsers: number;
  existingUsersChange: number;
  ordersOverTime: Array<{ time: string; today: number; yesterday: number }>;
  lastSevenDaysSales: Array<{ day: string; amount: number }>;
  totalItemsSold: number;
  topProducts: Array<{ id: number; name: string; image: string; price: number; unitsSold: number }>;
  recentTransactions: Array<{ id: number; name: string; date: string; amount: number; status: string }>;
}

const defaultFromDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
};
const defaultToDate = () => new Date().toISOString().slice(0, 10);

export const DashboardApiSlice = ApiService.injectEndpoints({
  endpoints: (builder) => ({
    getTotalRevenue: builder.query<string, { plant: string }>({
      query: ({ plant }) => ({
        url: `/Dashboard/getTotalRevenue?plant=${plant}`,
        method: "GET",
        responseHandler: "text",
      }),
    }),
    getTotalOrders: builder.query<string, { plant: string }>({
      query: ({ plant }) => ({
        url: `/Dashboard/getTotalOrders?plant=${plant}`,
        method: "GET",
        responseHandler: "text",
      }),
    }),
    getUniqueVisits: builder.query<string, { plant: string }>({
      query: ({ plant }) => ({
        url: `/Dashboard/getUniqueVisits?plant=${plant}`,
        method: "GET",
      }),
    }),
    getNewUser: builder.query<number, { plant: string }>({
      query: ({ plant }) => ({
        url: `/Dashboard/getNewUser?plant=${plant}`,
        method: "GET",
      }),
      transformResponse: (response: any) =>
        typeof response === "number" ? response : Number(response?.results) || 0,
    }),
    getExistingUser: builder.query<number, { plant: string }>({
      query: ({ plant }) => ({
        url: `/Dashboard/getExistingUser?plant=${plant}`,
        method: "GET",
      }),
      transformResponse: (response: any) =>
        typeof response === "number" ? response : Number(response?.results) || 0,
    }),
    getOrdersOverTime: builder.query<any, { plant: string; timeRange?: number }>({
      query: ({ plant, timeRange }) => ({
        url: `/Dashboard/getOrdersOverTime?plant=${plant}${timeRange !== undefined ? `&timeRange=${timeRange}` : ''}`,
        method: "GET",
      }),
    }),
    getLast7DaysSales: builder.query<any, { plant: string; timeRange?: number }>({
      query: ({ plant, timeRange }) => ({
        url: `/Dashboard/getLast7DaysSales?plant=${plant}${timeRange !== undefined ? `&timeRange=${timeRange}` : ''}`,
        method: "GET",
      }),
    }),
    getAllTopSelling: builder.query<any, { plant: string }>({
      query: ({ plant }) => ({
        url: `/Dashboard/getAllTopSelling?plant=${plant}`,
        method: "GET",
      }),
    }),
    getRecentTransactions: builder.query<any, { plant: string }>({
      query: ({ plant }) => ({
        url: `/Dashboard/getRecentTransactions?plant=${plant}`,
        method: "GET",
      }),
    }),
    getSalesCountByOrderStatus: builder.query<any, { plant: string; orderStatus?: string; fromDate?: string; toDate?: string }>({
      query: ({ plant, orderStatus = '', fromDate = '', toDate = '' }) => ({
        url: `/Dashboard/getSalesCountByOrderStatus?plant=${plant}&orderStatus=${orderStatus}&fromDate=${fromDate}&toDate=${toDate}`,
        method: "GET",
      }),
    }),
    getSalesValueByOrderStatus: builder.query<any, { plant: string; orderStatus?: string; fromDate?: string; toDate?: string }>({
      query: ({ plant, orderStatus = '', fromDate = '', toDate = '' }) => ({
        url: `/Dashboard/getSalesCountByOrderStatus?plant=${plant}&orderStatus=${orderStatus}&fromDate=${fromDate}&toDate=${toDate}`,
        method: "GET",
      }),
    }),
    getTodayOrderStatusSummary: builder.query<any, { plant: string }>({
      query: ({ plant }) => ({
        url: `/Dashboard/getTodayOrderStatusSummary?plant=${plant}`,
        method: "GET",
      }),
    }),
    getTodayOrderStatusDetails: builder.query<any, { plant: string; orderStatus: string }>({
      query: ({ plant, orderStatus }) => ({
        url: `/Dashboard/getTodayOrderStatusDetails?plant=${plant}&orderStatus=${orderStatus}`,
        method: "GET",
      }),
    }),
    getDashboardCategories: builder.query<any, { plant: string }>({
      query: ({ plant }) => ({
        url: `/Dashboard/getDashboardCategories?plant=${plant}`,
        method: "GET",
      }),
    }),
    getDashboardProducts: builder.query<any, { plant: string; search?: string }>({
      query: ({ plant, search }) => {
        let url = `/Dashboard/getDashboardProducts?plant=${plant}`;
        if (search) url += `&search=${search}`;
        return {
          url,
          method: "GET",
        };
      },
    }),
    getDashboardWarehouses: builder.query<any, { plant: string }>({
      query: ({ plant }) => ({
        url: `/Dashboard/getDashboardWarehouses?plant=${plant}`,
        method: "GET",
      }),
    }),
    getChannelPerformance: builder.query<any, { 
      plant: string; 
      fromDate: string; 
      toDate: string; 
      warehouse?: string; 
      categoryId?: string; 
      productId?: string;
    }>({
      query: ({ plant, fromDate, toDate, warehouse = '', categoryId = '', productId = '' }) => ({
        url: `/Dashboard/getChannelPerformance?plant=${plant}&fromDate=${fromDate}&toDate=${toDate}&warehouse=${warehouse}&categoryId=${categoryId}&productId=${productId}`,
        method: "GET",
      }),
    }),
    getOrdersByChannel: builder.query<any, { 
      plant: string; 
      fromDate: string; 
      toDate: string; 
      warehouse?: string; 
      categoryId?: string; 
      productId?: string;
    }>({
      query: ({ plant, fromDate, toDate, warehouse = '', categoryId = '', productId = '' }) => ({
        url: `/Dashboard/getOrdersByChannel?plant=${plant}&fromDate=${fromDate}&toDate=${toDate}&warehouse=${warehouse}&categoryId=${categoryId}&productId=${productId}`,
        method: "GET",
      }),
    }),
    getSalesByRegion: builder.query<any, { 
      plant: string; 
      fromDate: string; 
      toDate: string; 
      warehouse?: string; 
      categoryId?: string; 
      productId?: string;
    }>({
      query: ({ plant, fromDate, toDate, warehouse = '', categoryId = '', productId = '' }) => ({
        url: `/Dashboard/getSalesByRegion?plant=${plant}&fromDate=${fromDate}&toDate=${toDate}&warehouse=${warehouse}&categoryId=${categoryId}&productId=${productId}`,
        method: "GET",
      }),
    }),
    getSalesSummary: builder.query<any, { 
      plant: string; 
      fromDate: string; 
      toDate: string; 
      warehouse?: string; 
      categoryId?: string; 
      productId?: string;
      visitors?: number;
    }>({
      query: ({ plant, fromDate, toDate, warehouse = '', categoryId = '', productId = '', visitors = 0 }) => ({
        url: `/Dashboard/getSalesSummary?plant=${plant}&fromDate=${fromDate}&toDate=${toDate}&warehouse=${warehouse}&categoryId=${categoryId}&productId=${productId}&visitors=${visitors}`,
        method: "GET",
      }),
    }),
    getWarehouseStock: builder.query<any, { 
      plant: string; 
      warehouse?: string; 
      categoryId?: string; 
      productId?: string;
    }>({
      query: ({ plant, warehouse = '', categoryId = '', productId = '' }) => ({
        url: `/Dashboard/getWarehouseStock?plant=${plant}&warehouse=${warehouse}&categoryId=${categoryId}&productId=${productId}`,
        method: "GET",
      }),
    }),
    getInventorySummary: builder.query<any, { 
      plant: string; 
      warehouse?: string; 
      categoryId?: string; 
      productId?: string;
    }>({
      query: ({ plant, warehouse = '', categoryId = '', productId = '' }) => ({
        url: `/Dashboard/getInventorySummary?plant=${plant}&warehouse=${warehouse}&categoryId=${categoryId}&productId=${productId}`,
        method: "GET",
      }),
    }),
    getSalesOverview: builder.query<any, { 
      plant: string; 
      fromDate: string; 
      toDate: string;
      warehouse?: string; 
      categoryId?: string; 
      productId?: string;
    }>({
      query: ({ plant, fromDate, toDate, warehouse = '', categoryId = '', productId = '' }) => ({
        url: `/Dashboard/getSalesOverview?plant=${plant}&fromDate=${fromDate}&toDate=${toDate}&warehouse=${warehouse}&categoryId=${categoryId}&productId=${productId}`,
        method: "GET",
      }),
    }),
    getSearchAnalyticsLogs: builder.query<any, { 
      plant: string; 
      searchType?: string; 
      searchData?: string; 
      search?: string; 
      fromDate?: string; 
      toDate?: string; 
      selectedProductId?: string;
    }>({
      query: ({ plant, searchType, searchData, search, fromDate, toDate, selectedProductId }) => {
        let url = `/Dashboard/getSearchAnalyticsLogs?plant=${plant}`;
        if (searchType) url += `&searchType=${searchType}`;
        if (searchData) url += `&searchData=${searchData}`;
        if (search) url += `&search=${search}`;
        if (fromDate) url += `&fromDate=${fromDate}`;
        if (toDate) url += `&toDate=${toDate}`;
        if (selectedProductId) url += `&selectedProductId=${selectedProductId}`;
        return { url, method: "GET" };
      }
    }),
    getNoResultSearchAnalyticsLogs: builder.query<any, { 
      plant: string; 
      searchType?: string; 
      searchData?: string; 
      search?: string; 
      fromDate?: string; 
      toDate?: string; 
      selectedProductId?: string;
    }>({
      query: ({ plant, searchType, searchData, search, fromDate, toDate, selectedProductId }) => {
        let url = `/Dashboard/getNoResultSearchAnalyticsLogs?plant=${plant}`;
        if (searchType) url += `&searchType=${searchType}`;
        if (searchData) url += `&searchData=${searchData}`;
        if (search) url += `&search=${search}`;
        if (fromDate) url += `&fromDate=${fromDate}`;
        if (toDate) url += `&toDate=${toDate}`;
        if (selectedProductId) url += `&selectedProductId=${selectedProductId}`;
        return { url, method: "GET" };
      }
    }),
  }),
});

export const {
  useGetTotalRevenueQuery,
  useGetTotalOrdersQuery,
  useGetUniqueVisitsQuery,
  useGetNewUserQuery,
  useGetExistingUserQuery,
  useGetOrdersOverTimeQuery,
  useGetLast7DaysSalesQuery,
  useGetAllTopSellingQuery,
  useGetRecentTransactionsQuery,
  useGetSalesCountByOrderStatusQuery,
  useGetSalesValueByOrderStatusQuery,
  useGetTodayOrderStatusSummaryQuery,
  useGetTodayOrderStatusDetailsQuery,
  useGetDashboardCategoriesQuery,
  useGetDashboardProductsQuery,
  useGetDashboardWarehousesQuery,
  useGetChannelPerformanceQuery,
  useGetOrdersByChannelQuery,
  useGetSalesByRegionQuery,
  useGetSalesSummaryQuery,
  useGetWarehouseStockQuery,
  useGetSalesOverviewQuery,
  useGetInventorySummaryQuery,
  useGetSearchAnalyticsLogsQuery,
  useGetNoResultSearchAnalyticsLogsQuery,
} = DashboardApiSlice;